import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const KEY_THEME = 'liveclock-theme'; // 'system' | 'light' | 'dark'
const ThemeContext = createContext();

export function ThemeProvider({ children }) {
  const [mode, setMode] = useState(() => {
    const saved = localStorage.getItem(KEY_THEME);
    return saved === 'light' || saved === 'dark' || saved === 'system' ? saved : 'system';
  });

  const [systemPrefersDark, setSystemPrefersDark] = useState(false);

  // watch OS theme
  useEffect(() => {
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const sync = () => setSystemPrefersDark(mql.matches);
    sync();
    mql.addEventListener?.('change', sync);
    return () => mql.removeEventListener?.('change', sync);
  }, []);

  const isDark = mode === 'dark' || (mode === 'system' && systemPrefersDark);

  // expose theme to CSS via <html data-theme="...">
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  // persist
  useEffect(() => {
    localStorage.setItem(KEY_THEME, mode);
  }, [mode]);

  const value = useMemo(() => ({ mode, setMode, isDark }), [mode, isDark]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
